/** @format */

import {useMemo} from 'react';

import {AccountsCompanyMediaplanType} from 'graphql/generated/types';

import {useGetCampaignMediaplanDailyStatsQuery} from 'pages/Home/Main/Project/graphql/__generated__';

import {sortCampaignsBySpent} from 'helpers/campaignCards';

import {LastCheckType, SensorGroupsType, KpiType, Period} from '../types';
import {mapCampaignStatsToCards} from '../components/CampaignCards/helpers/mapCampaignStatsToCards';
import {MAX_VISIBLE_CARDS} from '../components/CampaignCards/constants';

interface UseCampaignCardsDataParams {
  period: Period;
  lastChecks: ReadonlyArray<LastCheckType>;
  sensorGroups: SensorGroupsType;
  mediaplanType?: AccountsCompanyMediaplanType | null;
  primaryKpi?: KpiType;
}

export const useCampaignCardsData = ({
  period,
  lastChecks,
  sensorGroups,
  mediaplanType,
  primaryKpi,
}: UseCampaignCardsDataParams) => {
  const {data, loading, error} = useGetCampaignMediaplanDailyStatsQuery({
    variables: {
      startDate: period.startDate,
      endDate: period.endDate,
    },
  });

  const dailyStats = data?.currentUser?.currentCompany?.campaignMediaplanDailyStats;

  const visibleCampaigns = useMemo(() => {
    if (!dailyStats) return [];

    const campaigns = mapCampaignStatsToCards({
      campaignStats: dailyStats,
      lastChecks,
      sensorGroups,
      mediaplanType,
      primaryKpi,
    });
    const sorted = sortCampaignsBySpent(campaigns);
    return sorted.slice(0, MAX_VISIBLE_CARDS);
  }, [dailyStats, lastChecks, sensorGroups, mediaplanType, primaryKpi]);

  const totalCampaignsCount = useMemo(() => {
    if (!dailyStats) return 0;

    const campaigns = mapCampaignStatsToCards({
      campaignStats: dailyStats,
      lastChecks,
      sensorGroups,
      mediaplanType,
      primaryKpi,
    });
    return campaigns.length;
  }, [dailyStats, lastChecks, sensorGroups, mediaplanType, primaryKpi]);

  return {
    visibleCampaigns,
    visibleCampaignsCount: visibleCampaigns.length,
    totalCampaignsCount,
    loading,
    error,
    dailyStats,
  };
};
